import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { IRecipe } from '../api/IRecipe';

export type DrawerMode = 'menu' | 'recipe' | 'search';

interface AIAssistantContextType {
  registerForm: (
    setRecipe: (recipe: IRecipe) => void,
    getRecipe: () => IRecipe | undefined
  ) => void;
  unregisterForm: () => void;
  populateForm: (proposal: Partial<IRecipe>) => void;
  getCurrentRecipe: () => Partial<IRecipe> | undefined;
  isFormRegistered: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  drawerMode: DrawerMode;
  setDrawerMode: (mode: DrawerMode) => void;
}

const AIAssistantContext = createContext<AIAssistantContextType>({
  registerForm: () => {},
  unregisterForm: () => {},
  populateForm: () => {},
  getCurrentRecipe: () => undefined,
  isFormRegistered: false,
  drawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  drawerMode: 'menu',
  setDrawerMode: () => {},
});

export const useAIAssistant = () => useContext(AIAssistantContext);

export const AIAssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isFormRegistered, setIsFormRegistered] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('menu');
  const setRecipeRef = useRef<((recipe: IRecipe) => void) | null>(null);
  const getRecipeRef = useRef<(() => IRecipe | undefined) | null>(null);

  const registerForm = useCallback(
    (setRecipe: (recipe: IRecipe) => void, getRecipe: () => IRecipe | undefined) => {
      setRecipeRef.current = setRecipe;
      getRecipeRef.current = getRecipe;
      setIsFormRegistered(true);
    },
    []
  );

  const unregisterForm = useCallback(() => {
    setRecipeRef.current = null;
    getRecipeRef.current = null;
    setIsFormRegistered(false);
  }, []);

  const populateForm = useCallback((proposal: Partial<IRecipe>) => {
    if (setRecipeRef.current && getRecipeRef.current) {
      const current = getRecipeRef.current();
      if (current) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setTimeout(() => {
          setRecipeRef.current?.({ ...current, ...proposal } as IRecipe);
        }, 50);
      }
    }
  }, []);

  const getCurrentRecipe = useCallback(() => {
    return getRecipeRef.current?.();
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerMode('menu');
  }, []);

  return (
    <AIAssistantContext.Provider
      value={{
        registerForm,
        unregisterForm,
        populateForm,
        getCurrentRecipe,
        isFormRegistered,
        drawerOpen,
        openDrawer,
        closeDrawer,
        drawerMode,
        setDrawerMode,
      }}
    >
      {children}
    </AIAssistantContext.Provider>
  );
};
